import React, { useState, useRef } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  Settings, ArrowLeft, Save, Trash2, Image, Calendar, FileText, 
  RefreshCw, Database, Plus, Upload
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { useAPI } from '../hooks/useAPI';
import { galleryAPI, bookingsAPI, contactsAPI } from '../services/api';
import api from '../services/api';

const AdminSettings = () => {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState('images');
  const [isSaving, setIsSaving] = useState({});
  const [isDeleting, setIsDeleting] = useState({});
  const [isUploading, setIsUploading] = useState({});
  const [editedItems, setEditedItems] = useState({});

  const { data: gallery, refetch: refetchGallery } = useAPI(() => galleryAPI.getAll());
  const { data: bookings, refetch: refetchBookings } = useAPI(() => bookingsAPI.getAll());
  const { data: contacts, refetch: refetchContacts } = useAPI(() => contactsAPI.getAll());

  const [newItem, setNewItem] = useState({ 
    service: '', 
    description: '',
    beforeImage: '', 
    afterImage: '' 
  });

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Upload fichier
  const uploadFile = async (file, itemId, field) => {
    const key = `${itemId}-${field}`;
    setIsUploading(prev => ({ ...prev, [key]: true }));
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const url = res.data.url;
      
      if (itemId === 'new') {
        setNewItem(prev => ({ ...prev, [field]: url }));
      } else {
        await api.put(`/gallery/${itemId}`, { [field]: url });
        refetchGallery();
      }
      
      toast({ title: "Image uploadée !" });
    } catch (err) {
      toast({ title: "Erreur", description: String(err), variant: "destructive" });
    } finally {
      setIsUploading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Mettre à jour un champ
  const handleFieldChange = (itemId, field, value) => {
    setEditedItems(prev => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] || {}),
        [field]: value
      }
    }));
  };

  // Sauvegarder les modifications
  const saveItem = async (itemId) => {
    const changes = editedItems[itemId];
    if (!changes) return;
    
    setIsSaving(prev => ({ ...prev, [itemId]: true }));
    try {
      await api.put(`/gallery/${itemId}`, changes);
      refetchGallery();
      setEditedItems(prev => {
        const newState = { ...prev };
        delete newState[itemId];
        return newState;
      });
      toast({ title: "Modifications sauvegardées !" });
    } catch (err) {
      toast({ title: "Erreur", variant: "destructive" });
    } finally {
      setIsSaving(prev => ({ ...prev, [itemId]: false }));
    }
  };

  // Ajouter item
  const addItem = async () => {
    if (!newItem.description) {
      toast({ title: "Entrez un titre", variant: "destructive" });
      return;
    }
    setIsSaving(prev => ({ ...prev, new: true }));
    try {
      await api.post('/gallery', newItem);
      setNewItem({ service: '', description: '', beforeImage: '', afterImage: '' });
      refetchGallery();
      toast({ title: "Réalisation ajoutée !" });
    } catch (err) {
      toast({ title: "Erreur", variant: "destructive" });
    } finally {
      setIsSaving(prev => ({ ...prev, new: false }));
    }
  };

  // Supprimer
  const deleteItem = async (id) => {
    if (!window.confirm('Supprimer cette réalisation ?')) return;
    setIsDeleting(prev => ({ ...prev, [id]: true }));
    try {
      await api.delete(`/gallery/${id}`);
      refetchGallery();
      toast({ title: "Supprimé !" });
    } catch (err) {
      toast({ title: "Erreur", variant: "destructive" });
    } finally {
      setIsDeleting(prev => ({ ...prev, [id]: false }));
    }
  };

  const deleteBooking = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/bookings/${id}`); refetchBookings(); toast({ title: "Supprimé !" }); } 
    catch (err) { toast({ title: "Erreur", variant: "destructive" }); }
  };

  const deleteContact = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await api.delete(`/contacts/${id}`); refetchContacts(); toast({ title: "Supprimé !" }); } 
    catch (err) { toast({ title: "Erreur", variant: "destructive" }); }
  };

  const deleteAllBookings = async () => {
    if (!window.confirm('Tout supprimer ?')) return;
    try { await api.delete('/bookings/all'); refetchBookings(); toast({ title: "Tout supprimé !" }); } 
    catch (err) { toast({ title: "Erreur", variant: "destructive" }); }
  };

  const deleteAllContacts = async () => {
    if (!window.confirm('Tout supprimer ?')) return;
    try { await api.delete('/contacts/all'); refetchContacts(); toast({ title: "Tout supprimé !" }); } 
    catch (err) { toast({ title: "Erreur", variant: "destructive" }); }
  };

  // Composant upload
  const UploadBtn = ({ itemId, field, currentUrl, label }) => {
    const ref = useRef(null);
    const key = `${itemId}-${field}`;
    const loading = isUploading[key];
    
    return (
      <div className="space-y-1">
        <Label className="text-xs text-gray-500">{label}</Label>
        <input
          ref={ref}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files[0] && uploadFile(e.target.files[0], itemId, field)}
        />
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => ref.current?.click()}
            disabled={loading}
            className="text-xs"
          >
            <Upload className="w-3 h-3 mr-1" />
            {loading ? 'Upload...' : 'Choisir'}
          </Button>
          {currentUrl && (
            <img src={currentUrl} alt="" className="w-12 h-12 object-cover rounded border" />
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <Link to="/admin/dashboard">
            <Button variant="outline" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
          </Link>
          <Settings className="w-6 h-6 text-purple-600" />
          <h1 className="text-xl font-bold">Réglages</h1>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Tabs */}
        <div className="border-b mb-6 flex gap-4">
          <button
            onClick={() => setActiveTab('images')}
            className={`pb-2 px-1 border-b-2 font-medium ${activeTab === 'images' ? 'border-purple-500 text-purple-600' : 'border-transparent text-gray-500'}`}
          >
            <Image className="w-4 h-4 inline mr-1" />
            Galerie
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`pb-2 px-1 border-b-2 font-medium ${activeTab === 'data' ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500'}`}
          >
            <Database className="w-4 h-4 inline mr-1" />
            Données
          </button>
        </div>

        {/* Galerie */}
        {activeTab === 'images' && (
          <div className="space-y-6">
            {gallery?.map((item, i) => {
              const edited = editedItems[item.id] || {};
              const hasChanges = Object.keys(edited).length > 0;
              
              return (
                <Card key={item.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base">Réalisation {i + 1}</CardTitle>
                      <div className="flex gap-2">
                        {hasChanges && (
                          <Button
                            size="sm"
                            onClick={() => saveItem(item.id)}
                            disabled={isSaving[item.id]}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Save className="w-4 h-4 mr-1" />
                            Sauvegarder
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteItem(item.id)}
                          className="text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Catégorie (badge bleu)</Label>
                        <Input
                          value={edited.service !== undefined ? edited.service : (item.service || '')}
                          onChange={(e) => handleFieldChange(item.id, 'service', e.target.value)}
                          placeholder="Ex: Lavage Extérieur"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label>Titre complet (affiché en gras)</Label>
                        <Input
                          value={edited.description !== undefined ? edited.description : (item.description || '')}
                          onChange={(e) => handleFieldChange(item.id, 'description', e.target.value)}
                          placeholder="Ex: BMW X5 - Nettoyage complet avec shampoinage"
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <UploadBtn 
                        itemId={item.id} 
                        field="beforeImage" 
                        currentUrl={item.beforeImage} 
                        label="Photo AVANT"
                      />
                      <UploadBtn 
                        itemId={item.id} 
                        field="afterImage" 
                        currentUrl={item.afterImage} 
                        label="Photo APRÈS"
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}

            {/* Ajouter nouvelle réalisation */}
            <Card className="border-dashed border-2 border-green-300">
              <CardHeader className="pb-2">
                <CardTitle className="text-base text-green-700">
                  <Plus className="w-4 h-4 inline mr-2" />
                  Ajouter une nouvelle réalisation
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Catégorie (badge bleu)</Label>
                    <Input
                      value={newItem.service}
                      onChange={(e) => setNewItem(prev => ({ ...prev, service: e.target.value }))}
                      placeholder="Ex: Lavage Extérieur"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Titre complet (affiché en gras)</Label>
                    <Input
                      value={newItem.description}
                      onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Ex: BMW X5 - Nettoyage complet avec shampoinage"
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <UploadBtn 
                    itemId="new" 
                    field="beforeImage" 
                    currentUrl={newItem.beforeImage} 
                    label="Photo AVANT"
                  />
                  <UploadBtn 
                    itemId="new" 
                    field="afterImage" 
                    currentUrl={newItem.afterImage} 
                    label="Photo APRÈS"
                  />
                </div>
                <Button 
                  onClick={addItem} 
                  disabled={isSaving.new} 
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Ajouter cette réalisation
                </Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Données */}
        {activeTab === 'data' && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex justify-between text-base">
                  <span><Image className="w-4 h-4 inline mr-2" />Galerie</span>
                  <Badge>{gallery?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {gallery?.map((item, i) => (
                  <div key={item.id} className="flex justify-between items-center p-2 bg-gray-50 rounded mb-2">
                    <span className="text-sm truncate flex-1">{i + 1}. {item.description || item.service || 'Sans titre'}</span>
                    <Button variant="ghost" size="sm" onClick={() => deleteItem(item.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {!gallery?.length && <p className="text-gray-500 text-sm text-center py-4">Aucune</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex justify-between text-base">
                  <span><Calendar className="w-4 h-4 inline mr-2" />Réservations</span>
                  <Badge>{bookings?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {bookings?.slice(0, 5).map((b) => (
                  <div key={b.id} className="flex justify-between items-center p-2 bg-gray-50 rounded mb-2">
                    <span className="text-sm truncate">{b.client?.firstName} {b.client?.lastName}</span>
                    <Button variant="ghost" size="sm" onClick={() => deleteBooking(b.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {bookings?.length > 0 && (
                  <Button variant="destructive" size="sm" className="w-full mt-2" onClick={deleteAllBookings}>
                    Tout supprimer
                  </Button>
                )}
                {!bookings?.length && <p className="text-gray-500 text-sm text-center py-4">Aucune</p>}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex justify-between text-base">
                  <span><FileText className="w-4 h-4 inline mr-2" />Messages</span>
                  <Badge>{contacts?.length || 0}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contacts?.slice(0, 5).map((c) => (
                  <div key={c.id} className="flex justify-between items-center p-2 bg-gray-50 rounded mb-2">
                    <span className="text-sm truncate">{c.firstName} {c.lastName}</span>
                    <Button variant="ghost" size="sm" onClick={() => deleteContact(c.id)} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                {contacts?.length > 0 && (
                  <Button variant="destructive" size="sm" className="w-full mt-2" onClick={deleteAllContacts}>
                    Tout supprimer
                  </Button>
                )}
                {!contacts?.length && <p className="text-gray-500 text-sm text-center py-4">Aucun</p>}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSettings;
